// src/components/map/TerritoriesPanel.js
import React from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Building2, MapPinned, Package, Settings2, UserX, Users } from "lucide-react";

export const TerritoriesPanel = ({
  territories,
  loading,
  error,
  selectedTerritoryId,
  statsByTerritoryId = {},
  summary = null,
  filters,
  onFiltersChange,
  onSelectTerritory,
}) => {
  const navigate = useNavigate();
  const handleSummaryFilterToggle = (key) => {
    if (!onFiltersChange) return;
    onFiltersChange((prev) => {
      const nextEnabled = !prev?.[key];
      return {
        ...prev,
        unassignedOnly: key === "unassignedOnly" ? nextEnabled : false,
        outsideTerritoryOnly: key === "outsideTerritoryOnly" ? nextEnabled : false,
      };
    });
  };

  return (
    <div className="flex h-full flex-col">
      {error ? <div className="bg-red-50 px-2 py-1 text-xs text-red-700">{error}</div> : null}
      {loading ? <div className="px-2 py-1 text-xs text-gray-500">Loading territories…</div> : null}

      <div className="border-b border-slate-200 px-3 py-2.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-slate-900">Territories</div>
            <p className="mt-0.5 text-xs text-slate-500">
              Highlight regions on the map here. Edit full territory details in Team.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate("/team?tab=territories")}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
          >
            <Settings2 className="h-3.5 w-3.5" />
            Edit territories
          </button>
        </div>
      </div>

      <div className="space-y-2 px-3 pt-2">
        {summary ? (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleSummaryFilterToggle("unassignedOnly")}
              className={`rounded-xl border px-2.5 py-2 text-left transition ${
                filters?.unassignedOnly
                  ? "border-amber-300 bg-amber-50"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100"
              }`}
            >
              <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                <UserX className="h-3 w-3" />
                Unassigned
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{summary.unassignedAccounts}</div>
              <div className="text-[11px] text-slate-500">accounts without reps</div>
            </button>
            <button
              type="button"
              onClick={() => handleSummaryFilterToggle("outsideTerritoryOnly")}
              className={`rounded-xl border px-2.5 py-2 text-left transition ${
                filters?.outsideTerritoryOnly
                  ? "border-rose-300 bg-rose-50"
                  : "border-slate-200 bg-slate-50 hover:bg-slate-100"
              }`}
            >
              <div className="flex items-center gap-1 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                <MapPinned className="h-3 w-3" />
                Uncovered
              </div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{summary.uncoveredAccounts}</div>
              <div className="text-[11px] text-slate-500">outside all territories</div>
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex-1 space-y-0.5 overflow-y-auto p-3">
        {territories.length === 0 && !loading ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-xs text-gray-500">
            No territories yet. Use Team → Territories to create and organize them.
          </div>
        ) : null}

        {territories.map((territory) => {
          const selected = selectedTerritoryId === territory.id;
          const stats = statsByTerritoryId?.[territory.id] || {
            accounts: 0,
            samples: 0,
            converted: 0,
            unassigned: 0,
            reps: 0,
            sampleAccounts: 0,
          };

          return (
            <button
              key={territory.id}
              type="button"
              onClick={() => onSelectTerritory && onSelectTerritory(territory.id)}
              className={`w-full rounded-xl border px-3 py-1 text-left transition ${
                selected
                  ? "border-blue-300 bg-blue-50/80 shadow-sm"
                  : "border-gray-200 bg-white hover:bg-slate-50"
              }`}
              title="Click to highlight this territory on the map"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-gray-800">
                  {territory.name || "Untitled territory"}
                </span>
                <div className="flex items-center gap-1">
                  <span className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-1.5 py-0.5 text-[10px] text-gray-700">
                    <Building2 className="h-3 w-3" />
                    {Number(stats.accounts || 0)}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] text-blue-700">
                    <Package className="h-3 w-3" />
                    {Number(stats.samples || 0)}
                  </span>
                </div>
              </div>

              <div className="mt-1 grid grid-cols-3 gap-1 text-[10px]">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-1.5 py-1 text-slate-700">
                  <div className="flex items-center gap-1">
                    <Users className="h-3 w-3" />
                    <span className="font-medium">{Number(stats.reps || 0)}</span>
                  </div>
                  <div className="mt-0.5 text-slate-500">reps</div>
                </div>
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-1.5 py-1 text-amber-700">
                  <div className="flex items-center gap-1">
                    <UserX className="h-3 w-3" />
                    <span className="font-medium">{Number(stats.unassigned || 0)}</span>
                  </div>
                  <div className="mt-0.5 text-amber-600">unassigned</div>
                </div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-emerald-700">
                  <div className="flex items-center gap-1">
                    <BadgeCheck className="h-3 w-3" />
                    <span className="font-medium">{Number(stats.converted || 0)}</span>
                  </div>
                  <div className="mt-0.5 text-emerald-600">converted</div>
                </div>
              </div>

              <div className="mt-1 flex flex-wrap gap-1">
                {Number(stats.sampleAccounts || 0) > 0 ? (
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-blue-700">
                    {Number(stats.sampleAccounts || 0)} sampled accounts
                  </span>
                ) : null}
                {Array.isArray(territory.states) && territory.states.length > 0 ? (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                    {territory.states.length} states
                  </span>
                ) : null}
                {Array.isArray(territory.cities) && territory.cities.length > 0 ? (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                    {territory.cities.length} cities
                  </span>
                ) : null}
                {Array.isArray(territory.zipCodes) && territory.zipCodes.length > 0 ? (
                  <span className="rounded-full border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-[10px] font-medium leading-none text-slate-600">
                    {territory.zipCodes.length} ZIPs
                  </span>
                ) : null}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
